import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextInput, Text, Badge, Popover, Tooltip, UnstyledButton } from '@mantine/core';
import {
    IconUserPlus,
    IconUser,
    IconSearch,
    IconClock,
    IconUsers,
    IconPencil,
    IconChevronDown,
    IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { EMPTY_USER, User } from '@turizi/shared';
import GenericActionButton from '@/components/generic-action-button';
import { useUsers, useCreateUser, useBusinesses } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import CardModal from '@/components/card-modal';
import { localUser } from '@/services/session-service';
import { MENU_PERMISSIONS_LIST } from '@/components/left-menu';
import { CREATE_ACTION_PERMISSIONS_LIST } from '@/components/create-actions-menu';
import { formatDate, formatRelativeTime } from '@/lib/format-utils';
import UserPermissionsSelector from '@/components/user-permissions-selector';
import { SETTINGS_USER_EDIT_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';


export default function UsersPage() {
    const { t } = useTranslation();
    const currentUser = localUser();
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newUser, setNewUser] = useState<User>(EMPTY_USER);

    const { data: usersResponse, isLoading } = useUsers();
    const { data: businessesResponse } = useBusinesses();
    const allBusinesses = businessesResponse?.data || [];
    const createUserMutation = useCreateUser();

    const users = usersResponse?.data || [];

    const filteredUsers = users
        .filter((u) => {
            const term = searchTerm.toLowerCase().trim();
            if (!term) return true;
            const nameMatch = u.name?.toLowerCase().includes(term);
            const emailMatch = u.email?.toLowerCase().includes(term);
            const tenantMatch = u.tenant?.name?.toLowerCase().includes(term);
            return nameMatch || emailMatch || tenantMatch;
        })
        .sort((a, b) => {
            const aName = a.name?.toLowerCase() || '';
            const bName = b.name?.toLowerCase() || '';
            return aName.localeCompare(bName);
        });

    const createUser = async () => {
        if (!newUser.email || !newUser.password) {
            return;
        }
        createUserMutation.mutate(
            { ...newUser, role: 'internal' },
            {
                onSuccess: () => {
                    setShowCreateModal(false);
                    setNewUser(EMPTY_USER);
                    notifications.show({
                        title: t('success'),
                        message: t('user_updated_successfully'),
                        color: 'green',
                    });
                },
                onError: (err: any) => {
                    notifications.show({
                        title: t('error'),
                        message: typeof err === 'string' ? err : err?.message || t('unknown_error'),
                        color: 'red',
                    });
                },
            }
        );
    };

    const getPermissionsSummary = (user: User) => {
        const allowedMenuItems = MENU_PERMISSIONS_LIST.filter((item) => user.permissions?.[item.key] !== false);
        const allowedCreateItems = CREATE_ACTION_PERMISSIONS_LIST.filter((item) => user.permissions?.[item.key] !== false);

        const isAllMenuAllowed = allowedMenuItems.length === MENU_PERMISSIONS_LIST.length;
        const isAllCreateAllowed = allowedCreateItems.length === CREATE_ACTION_PERMISSIONS_LIST.length;

        const isLimitedBusinesses = user.permissions?.reservation_business_mode === 'selected';
        const allowedBusinessIds: number[] = user.permissions?.allowed_reservation_business_ids || [];
        const isLimitedEdit = user.permissions?.edit_reservation_mode === 'created_only';
        const isGroupEdit = user.permissions?.edit_reservation_mode === 'group';
        const editionGroup = user.permissions?.edition_group;
        const isPriceCostDisabled = user.permissions?.edit_prices_and_costs_manually === false;

        if (isAllMenuAllowed && isAllCreateAllowed && !isLimitedBusinesses && !isLimitedEdit && !isGroupEdit && !isPriceCostDisabled) {
            return (
                <Popover position="bottom-start" withArrow shadow="md" withinPortal>
                    <Popover.Target>
                        <UnstyledButton className="cursor-pointer">
                            <Badge
                                color="emerald"
                                variant="light"
                                size="sm"
                                radius="md"
                                className="font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                            >
                                {t('full_access')}
                                <IconChevronDown size={11} className="inline ml-1 opacity-70" />
                            </Badge>
                        </UnstyledButton>
                    </Popover.Target>
                    <Popover.Dropdown className="max-w-xs p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-bold text-gray-800 mb-1 flex items-center gap-1.5">
                            <IconCheck size={14} className="text-emerald-600" />
                            <span>{t('full_access')}</span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed m-0">
                            {t('full_access_desc')}
                        </p>
                    </Popover.Dropdown>
                </Popover>
            );
        }

        return (
            <div className="flex flex-wrap items-center gap-1.5">
                {/* Menu permissions */}
                <Popover position="bottom-start" withArrow shadow="md" withinPortal>
                    <Popover.Target>
                        <UnstyledButton className="cursor-pointer">
                            <Badge
                                color="blue"
                                variant="light"
                                size="sm"
                                radius="md"
                                className="cursor-pointer hover:opacity-80 transition-opacity font-medium"
                            >
                                {isAllMenuAllowed
                                    ? `${t('menu_permissions')}: ${t('all')}`
                                    : `${t('menu_permissions')}: ${allowedMenuItems.length}/${MENU_PERMISSIONS_LIST.length}`}
                                <IconChevronDown size={11} className="inline ml-1 opacity-70" />
                            </Badge>
                        </UnstyledButton>
                    </Popover.Target>
                    <Popover.Dropdown className="max-w-md p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-bold text-gray-800 mb-2 flex items-center justify-between border-b border-gray-100 pb-1.5">
                            <span>{t('allowed_views')}</span>
                            <span className="text-gray-500 font-normal">
                                {allowedMenuItems.length} / {MENU_PERMISSIONS_LIST.length}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1 max-h-56 overflow-y-auto">
                            {allowedMenuItems.length === 0 ? (
                                <span className="text-xs text-gray-400 italic">{t('none')}</span>
                            ) : (
                                allowedMenuItems.map((item) => (
                                    <Badge key={item.key} color="blue" variant="light" size="xs" radius="sm">
                                        {t(item.labelKey)}
                                    </Badge>
                                ))
                            )}
                        </div>
                    </Popover.Dropdown>
                </Popover>

                {/* Create Action permissions */}
                <Popover position="bottom-start" withArrow shadow="md" withinPortal>
                    <Popover.Target>
                        <UnstyledButton className="cursor-pointer">
                            <Badge
                                color="teal"
                                variant="light"
                                size="sm"
                                radius="md"
                                className="cursor-pointer hover:opacity-80 transition-opacity font-medium"
                            >
                                {isAllCreateAllowed
                                    ? `${t('create_permissions')}: ${t('all')}`
                                    : `${t('create_permissions')}: ${allowedCreateItems.length}/${CREATE_ACTION_PERMISSIONS_LIST.length}`}
                                <IconChevronDown size={11} className="inline ml-1 opacity-70" />
                            </Badge>
                        </UnstyledButton>
                    </Popover.Target>
                    <Popover.Dropdown className="max-w-md p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-bold text-gray-800 mb-2 flex items-center justify-between border-b border-gray-100 pb-1.5">
                            <span>{t('allowed_create_actions')}</span>
                            <span className="text-gray-500 font-normal">
                                {allowedCreateItems.length} / {CREATE_ACTION_PERMISSIONS_LIST.length}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1 max-h-56 overflow-y-auto">
                            {allowedCreateItems.length === 0 ? (
                                <span className="text-xs text-gray-400 italic">{t('none')}</span>
                            ) : (
                                allowedCreateItems.map((item) => (
                                    <Badge key={item.key} color="teal" variant="light" size="xs" radius="sm">
                                        {t(item.labelKey)}
                                    </Badge>
                                ))
                            )}
                        </div>
                    </Popover.Dropdown>
                </Popover>

                {/* Business scope */}
                {isLimitedBusinesses && (
                    allowedBusinessIds.length === 0 ? (
                        <Badge color="amber" variant="light" size="sm" radius="md" className="font-medium">
                            {t('no_businesses_allowed')}
                        </Badge>
                    ) : allowedBusinessIds.length === 1 ? (
                        <Badge key={allowedBusinessIds[0]} color="amber" variant="light" size="sm" radius="md" className="font-medium">
                            {allBusinesses.find((b) => b.id === allowedBusinessIds[0])?.name || `#${allowedBusinessIds[0]}`}
                        </Badge>
                    ) : (
                        <Popover position="bottom-start" withArrow shadow="md" withinPortal>
                            <Popover.Target>
                                <UnstyledButton className="cursor-pointer">
                                    <Badge
                                        color="amber"
                                        variant="light"
                                        size="sm"
                                        radius="md"
                                        className="cursor-pointer hover:opacity-80 transition-opacity font-medium"
                                    >
                                        {allowedBusinessIds.length} {t('businesses')}
                                        <IconChevronDown size={11} className="inline ml-1 opacity-70" />
                                    </Badge>
                                </UnstyledButton>
                            </Popover.Target>
                            <Popover.Dropdown className="max-w-xs p-3" onClick={(e) => e.stopPropagation()}>
                                <div className="text-xs font-bold text-gray-800 mb-2 flex items-center justify-between border-b border-gray-100 pb-1.5">
                                    <span>{t('allowed_businesses')}</span>
                                    <span className="text-gray-500 font-normal">{allowedBusinessIds.length}</span>
                                </div>
                                <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto">
                                    {allowedBusinessIds.map((id) => {
                                        const bus = allBusinesses.find((b) => b.id === id);
                                        return (
                                            <Badge key={id} color="amber" variant="light" size="xs" radius="sm">
                                                {bus ? bus.name : `#${id}`}
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </Popover.Dropdown>
                        </Popover>
                    )
                )}

                {/* Edit scope */}
                {isLimitedEdit && (
                    <Badge color="orange" variant="light" size="sm" radius="md" className="font-medium">
                        {t('only_created_reservations')}
                    </Badge>
                )}
                {isGroupEdit && (
                    <Badge color="cyan" variant="light" size="sm" radius="md" className="font-medium">
                        <span className="flex items-center gap-1">
                            <IconUsers size={12} />
                            <span>{editionGroup ? `${t('edition_group')}: ${editionGroup}` : t('edition_group_reservations')}</span>
                        </span>
                    </Badge>
                )}

                {/* Manual price & cost */}
                {isPriceCostDisabled && (
                    <Badge color="red" variant="light" size="sm" radius="md" className="font-medium">
                        {t('no_manual_price_cost_access')}
                    </Badge>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-screen-xl">
            <div className="panel-content space-y-4">
                {/* Search & Actions Top Bar */}
                <div className="flex items-center justify-between gap-4">
                    <TextInput
                        placeholder={t('search_users')}
                        leftSection={<IconSearch size={16} className="text-gray-400" />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size='sm'
                    />
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        leftSection={<IconUserPlus size={16} stroke={1.5} />}
                        color='black'
                        size='xs'
                        variant='light'
                    >
                        {t('create_user')}
                    </Button>
                </div>

                {/* User List View */}
                {isLoading ? (
                    <SkeletonList />
                ) : filteredUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-gray-200/80 text-center space-y-3 shadow-xs">
                        <div className="p-4 rounded-full bg-gray-100/80 text-gray-400">
                            <IconUsers size={36} />
                        </div>
                        <div>
                            <Text fw={700} size="md" className="text-gray-800">
                                {t('no_users_found')}
                            </Text>
                            <Text size="xs" className="text-gray-500 max-w-sm mt-0.5">
                                {t('no_users_found_desc')}
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filteredUsers.map((user, index) => {
                            const isSelf = currentUser?.id === user.id;
                            const relativeLogin = user.last_login ? formatRelativeTime(user.last_login) : '';
                            const hasLastLogin = Boolean(user.last_login && relativeLogin && relativeLogin !== 'Invalid Date');

                            return (
                                <div
                                    key={user.id}
                                    className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-4 py-2.5 bg-white rounded-xl border border-gray-200/80 hover:border-gray-300 hover:shadow-2xs transition-all duration-150"
                                >
                                    {/* Left: Avatar & Identity Details */}
                                    <div className="flex items-center gap-3 min-w-0 md:w-100 shrink-0">
                                        <div
                                            className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 transition-colors ${isSelf
                                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                                : 'bg-gray-100 text-gray-700 border border-gray-200/70'
                                                }`}
                                        >
                                            {index + 1}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <Link
                                                    to={SETTINGS_USER_EDIT_PATH.replace(':id', String(user.id))}
                                                    className="whitespace-nowrap font-semibold text-sm text-gray-900 hover:text-yellow-700 transition-colors"
                                                    title={user.name}
                                                >
                                                    {user.name || '-'}
                                                </Link>
                                                {isSelf && (
                                                    <Badge color="yellow" variant="light" size="xs" radius="sm">
                                                        {t('you')}
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="whitespace-nowrap text-xs text-gray-500 mt-0.5">
                                                {user.email}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Middle: Permissions Popovers */}
                                    <div className="flex-1 min-w-0 flex items-center justify-start">
                                        {getPermissionsSummary(user)}
                                    </div>

                                    {/* Right: Last Login + Edit Button */}
                                    <div className="flex items-center justify-between md:justify-end gap-3.5 shrink-0 pt-2 md:pt-0 border-t border-gray-100 md:border-t-0">
                                        <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium min-w-[110px] justify-start md:justify-end">
                                            {/* <IconClock size={13} className="text-gray-400 shrink-0" /> */}
                                            {hasLastLogin ? (
                                                <Tooltip label={formatDate(user.last_login)} withArrow position="top">
                                                    <span className="cursor-help hover:text-gray-700 transition-colors whitespace-nowrap">
                                                        {relativeLogin}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="text-gray-200 italic whitespace-nowrap">{t('never_logged_in')}</span>
                                            )}
                                        </div>

                                        <Button
                                            component={Link}
                                            to={SETTINGS_USER_EDIT_PATH.replace(':id', String(user.id))}
                                            variant="subtle"
                                            color='black'
                                            size="xs"
                                            radius="md"
                                        >
                                            <IconPencil size={13} stroke={1.8} />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create User Modal */}
            <CardModal
                opened={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    setNewUser(EMPTY_USER);
                }}
                header={t('create_user')}
                size="750px"
            >
                <div className="p-5 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <TextInput
                            label={t('name')}
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            required
                            type="text"
                        />

                        <TextInput
                            label={t('email')}
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            required
                            type="email"
                        />

                        <TextInput
                            label={t('password')}
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            required
                            type="password"
                        />
                    </div>

                    <UserPermissionsSelector
                        permissions={newUser.permissions}
                        onChange={(permissions) => setNewUser({ ...newUser, permissions })}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => {
                                setShowCreateModal(false);
                                setNewUser(EMPTY_USER);
                            }}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            onClick={createUser}
                            loading={createUserMutation.isPending}
                            disabled={!newUser.email || !newUser.password}
                        >
                            {t('create')}
                        </Button>
                    </div>
                </div>
            </CardModal>
        </div>
    );
}